// src/components/LayerSelection.js
import React from "react";
import { connect, useDispatch } from "react-redux";

import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { setActiveField, toggleLayerState } from "../actions";
import { FEATURES } from "../utils/config";
import preloaded from "../utils/data.json";
import { generateSld } from "../utils/style-generator";
import { ColorUtils, calculateFieldStats, calculatePercentiles } from "../utils/helpers";

import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

import Box from "@mui/material/Box";

const mapStateToProps = (state) => ({
  map: state.MapReducer,
});

const FOLK_DANCES_LAYER_ID = "folk_dances_archive";

// UI-only, English labels
const FOLK_DANCES_GROUPS = [
  {
    id: "film",
    title: "Film materials",
    items: [
      { id: "film_process_total", label: "Number of recorded dance processes", unit: "count" },
      { id: "film_process_digitized", label: "Digitized dance processes", unit: "count" },
      { id: "film_process_available", label: "Available dance processes", unit: "count" },
      { id: "film_process_available_duration", label: "Total duration of available processes", unit: "hh:mm:ss" },
      { id: "cs_dancer_name", label: "Citizen science – dancer name occurrences", unit: "count" },
      { id: "cs_dance_name", label: "Citizen science – dance name occurrences", unit: "count" },
    ],
  },
  {
    id: "manuscripts",
    title: "Manuscripts",
    items: [
      { id: "ms_relevant_docs", label: "Number of relevant documents", unit: "count" },
      { id: "ms_pages", label: "Total number of pages", unit: "count" },
    ],
  },
  {
    id: "photos",
    title: "Photographs",
    items: [
      { id: "photo_total", label: "Total number of photographs", unit: "count" },
      { id: "photo_bw", label: "Black-and-white photographs", unit: "count" },
      { id: "photo_color", label: "Color photographs", unit: "count" },
    ],
  },
  {
    id: "dance_catalog",
    title: "Dance catalog",
    items: [{ id: "catalog_cards", label: "Number of descriptive cards", unit: "count" }],
  },
  {
    id: "ethnic_groups",
    title: "Ethnic groups",
    items: [
      { id: "ethn_coexisting_total", label: "Number of coexisting ethnic groups", unit: "count" },
      { id: "ethn_overview", label: "Ethnic overview", unit: "Yes / No" },
      { id: "ethn_hungarian", label: "Hungarian", unit: "count" },
      { id: "ethn_romanian", label: "Romanian", unit: "count" },
      { id: "ethn_slovak", label: "Slovak", unit: "count" },
      { id: "ethn_german", label: "German", unit: "count" },
      { id: "ethn_roma", label: "Roma", unit: "count" },
      { id: "ethn_serbian", label: "Serbian", unit: "count" },
      { id: "ethn_croatian", label: "Croatian", unit: "count" },
    ],
  },
  {
    id: "collectors",
    title: "Collectors",
    items: [
      { id: "collector_total", label: "Number of collectors active in the settlement", unit: "count" },
      { id: "collector_overview", label: "Collectors overview", unit: "Yes / No" },
      // individual collectors: later (dynamic)
    ],
  },
];

// Fő cím – main layer
function getMainTitle(map) {
  const isGrid =
    (map?.activeLayer || "").includes("grid") ||
    (map?.activeLayer || "").includes("kulonb");
  if (!isGrid) {
    if (map?.activeField) {
      const f = preloaded.find((o) => o.name === map.activeField);
      return f?.title || map.activeField || "";
    }
    return "";
  }
  const short = (map?.activeLayer || "").replace("allum_project:", "");
  const f = preloaded.find((o) => o.name === short);
  return f?.title || short || "";
}

// Alcím a 2. réteghez
function getDualTitleFromOverrides({ fieldName, layerId }) {
  if (layerId && (layerId.includes("grid") || layerId.includes("kulonb"))) {
    const short = layerId.replace("allum_project:", "");
    const f = preloaded.find((o) => o.name === short);
    return f?.title || short || "Second layer";
  }
  if (fieldName) {
    const f = preloaded.find((o) => o.name === fieldName);
    return f?.title || fieldName || "Second layer";
  }
  return "Second layer";
}

function LayerSelection(props) {
  const dispatch = useDispatch();

  const [targetLayer, setTargetLayer] = React.useState(1); // 1 = Main, 2 = Second
  const [showDual, setShowDual] = React.useState(
    typeof window !== "undefined" && !!window.__dualLayer?.enabled
  );
  const [dualTick, setDualTick] = React.useState(0);
  const [folkDanceValue, setFolkDanceValue] = React.useState("");

  React.useEffect(() => {
    const onDual = () => setDualTick((t) => t + 1);
    if (typeof window !== "undefined") {
      window.addEventListener("dualLayerChanged", onDual);
      return () => window.removeEventListener("dualLayerChanged", onDual);
    }
  }, []);

  const selectedThematic =
    targetLayer === 1
      ? props.map?.activeField || ""
      : (typeof window !== "undefined" && window.__dualLayer?.fieldName) ||
        props.map?.activeField ||
        "";

  const selectedGrid =
    targetLayer === 1
      ? props.map?.activeLayer || ""
      : (typeof window !== "undefined" && window.__dualLayer?.layerId) ||
        props.map?.activeLayer ||
        "";

  // melyik layer tematikáját mutatjuk a listában
  const thematicLayerId = React.useMemo(() => {
    if (!showDual || targetLayer === 1) return props.map.activeLayer;
    if (typeof window !== "undefined") {
      const d = window.__dualLayer || {};
      if (d.baseLayerId) return d.baseLayerId;
      if (d.layerId) return d.layerId;
    }
    return props.map.activeLayer;
  }, [showDual, targetLayer, props.map.activeLayer, dualTick]);

  const isGridThematic =
    (thematicLayerId || "").includes("grid") ||
    (thematicLayerId || "").includes("kulonb");

  // kategóriák előkészítése
  const categories = {};
  for (const item of preloaded) {
    if (item.category in categories) categories[item.category].push(item);
    else categories[item.category] = [item];
  }
  const cat = {};

  const DualHeader =
    FEATURES?.dualLayerMode && typeof window !== "undefined" ? (
      <Paper
        elevation={0}
        style={{
          margin: "8px 0 12px 0",
          padding: 8,
          background: "#f7f7f7",
          borderRadius: 8,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showDual}
              onChange={(e) => {
                const on = e.target.checked;
                setShowDual(on);
                window.__dualLayer = window.__dualLayer || {};
                window.__dualLayer.enabled = on;
                if (!on) {
                  setTargetLayer(1);
                  window.__dualLayerTarget = 1;
                }
                window.dispatchEvent(
                  new CustomEvent("dualLayerChanged", {
                    detail: { reason: "toggleSecond", enabled: on },
                  })
                );
              }}
            />
          }
          label="2nd layer"
        />

        {showDual && (
          <>
            <Typography variant="body2" gutterBottom style={{ marginTop: 6 }}>
              <b>Target layer</b>
            </Typography>
            <Stack direction="row" spacing={1} style={{ marginBottom: 6 }}>
              <Button
                size="small"
                variant={targetLayer === 1 ? "contained" : "outlined"}
                onClick={() => {
                  setTargetLayer(1);
                  if (typeof window !== "undefined") {
                    window.__dualLayerTarget = 1;
                    window.dispatchEvent(
                      new CustomEvent("dualLayerChanged", {
                        detail: { reason: "targetChanged", target: 1 },
                      })
                    );
                  }
                }}
                title={getMainTitle(props.map) || "Main layer"}
              >
                ① Main layer
              </Button>

              <Button
                size="small"
                variant={targetLayer === 2 ? "contained" : "outlined"}
                onClick={() => {
                  setTargetLayer(2);
                  if (typeof window !== "undefined") {
                    window.__dualLayerTarget = 2;
                    window.dispatchEvent(
                      new CustomEvent("dualLayerChanged", {
                        detail: { reason: "targetChanged", target: 2 },
                      })
                    );
                  }
                }}
                title={
                  (window.__dualLayer?.title ||
                    getDualTitleFromOverrides({
                      fieldName: window.__dualLayer?.fieldName,
                      layerId: window.__dualLayer?.layerId,
                    })) ?? "Second layer"
                }
              >
                ② Second layer
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    ) : null;

  return (
    <div style={{ marginTop: 16 }}>
      {DualHeader}

      <Typography variant="caption" gutterBottom component="div">
        Thematic maps
      </Typography>

        {/* ------------------------------------------------------------
            Folk dances archive – UI-only accordion (Step 1)
            ------------------------------------------------------------ */}
        {thematicLayerId === FOLK_DANCES_LAYER_ID && (
        <Box>
            {FOLK_DANCES_GROUPS.map((g) => (
            <Accordion key={g.id} >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{g.title}</Typography>
                </AccordionSummary>

                <AccordionDetails>
                <RadioGroup
                    value={folkDanceValue}
                    onChange={(e) => {
                    const v = e.target.value;
                    setFolkDanceValue(v);

                    // későbbi bekötési pont
                    if (typeof window !== "undefined") {
                        window.dispatchEvent(
                        new CustomEvent("folkDanceSelected", {
                            detail: {
                            id: v,
                            group: g.id,
                            },
                        })
                        );
                    }
                    }}
                >
                    {g.items.map((it) => (
                    <div>
                        <Divider />
                        <FormControlLabel
                            key={it.id}
                            value={it.id}
                            style={{ marginLeft: 0, marginTop: 10, marginBottom: 10 }}
                            control={<Radio size="small" style={{ display: "block" }} />}
                            label={`${it.label}${it.unit ? ` [${it.unit}]` : ""}`}
                        />
                    </div>
                    ))}
                </RadioGroup>
                </AccordionDetails>
            </Accordion>
            ))}
        </Box>
        )}



    {thematicLayerId !== FOLK_DANCES_LAYER_ID && (
      <div>
        {Object.entries(categories).map((entry) => {
          const category = entry[0];
          const itemList = entry[1];

          // --------------------------
          // 1) NEM-GRID tematikák (mezők: settlements_ ...)
          // --------------------------
          if (!isGridThematic) {
            const thematicLayer =
              thematicLayerId && props.map.layers[thematicLayerId]
                ? props.map.layers[thematicLayerId]
                : null;
            const choloplethFields = thematicLayer?.choloplethFields || [];

            {
              itemList.map((item) =>
                choloplethFields.map((cf) => {
                  if (item.name === cf) {
                    if (item.category in cat) cat[category].push(item);
                    else cat[category] = [item];
                  }
                })
              );
            }

            if (category !== "undefined" && cat[category]?.length > 0) {
              return (
                <Accordion key={category}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {category}
                  </AccordionSummary>
                  <AccordionDetails>
                    <RadioGroup
                      aria-labelledby="thematic-fields"
                      name="thematic-fields"
                      value={selectedThematic}
                      onChange={(e) => {
                        const v = e.target.value;

                        // MAIN – szokásos mezőváltás
                        if (targetLayer === 1 || !showDual) {
                          dispatch(setActiveField(v));
                          return;
                        }

                        // SECOND – NEM-GRID poligon tematikák
                        if (typeof window === "undefined") return;

                        const layerId = thematicLayerId || props.map.activeLayer;
                        const layerCfg = props.map.layers?.[layerId];
                        const url = layerCfg?.url || window.__dualLayer?.layerUrl || null;

                        const isFolkBaseLayer = typeof layerId === "string" && (layerId.includes("folk_base_layer") || layerId.includes("tanc_base_layer"));                        

                        // ✅ A stílustípus kizárólag a fieldStyles alapján dől el
                        const fs = layerCfg?.fieldStyles || {};
                        const fieldType =
                          Object.keys(fs).find((k) => Array.isArray(fs[k]) && fs[k].includes(v)) ||
                          "range";

                        const meta = preloaded.find((o) => o.name === v) || {};
                        const metaBreaks = Array.isArray(meta.breaks) ? meta.breaks.slice() : [];
                        const metaColors = Array.isArray(meta.colors) ? meta.colors.slice() : [];

                        const applySecond = (sldId, legendItems) => {
                          window.__dualLayer = {
                            ...(window.__dualLayer || {}),
                            enabled: true,
                            layerId,
                            layerUrl: url,
                            fieldName: v,
                            ...(sldId ? { sldId } : {}),
                            legend: { items: legendItems || [] },
                          };

                          window.dispatchEvent(
                            new CustomEvent("dualLayerChanged", {
                              detail: {
                                reason: "secondPoligonChanged",
                                layerId,
                                layerUrl: url,
                                fieldName: v,
                                sldId: sldId || null,
                              },
                            })
                          );

                          window.dispatchEvent(
                            new CustomEvent("secondLegendChanged", {
                              detail: { layerId, fieldName: v, sldId: sldId || null },
                            })
                          );
                        };

                        const saveSldAndApply = (styleObj, sldKind) => {
                          const sld = generateSld(
                            {
                              qualifiedName: layerId,
                              name: layerId.split(":")[1],
                              fieldName: v,
                              style: styleObj,
                            },
                            sldKind
                          );

                          fetch("http://193.225.239.5/backend2/saveSld", {
                            method: "POST",
                            headers: { Accept: "application/json", "Content-Type": "application/json" },
                            body: JSON.stringify({ sldData: sld }),
                          })
                            .then((r) => r.json())
                            .then((r) => {
                              if (!r.error && r.data?.id) {
                                applySecond(
                                  r.data.id,
                                  styleObj.breaks.map((b) => ({ name: b.name, color: b.color }))
                                );
                              } else {
                                console.warn("Second non-grid SLD save error", r);
                                applySecond(null, []);
                              }
                            })
                            .catch((err) => {
                              console.warn("Second non-grid SLD save failed", err);
                              applySecond(null, []);
                            });
                        };

                        // --------------------------
                        // ✅ SECOND CATEGORICAL: úgy, mint a main (meta.breaks/meta.colors), nem range!
                        // --------------------------
                        if (fieldType === "categorical") {
                          // 1) meta alapján
                          if (metaBreaks.length && metaColors.length) {
                            const breaks = metaBreaks
                              .map((val, idx) => {
                                const label = String(val);
                                if (!label || label === "NaN") return null;
                                return {
                                  name: label,
                                  values: [val, val],
                                  color: metaColors[idx % metaColors.length],
                                  type: "categorical",
                                };
                              })
                              .filter(Boolean);

                            if (!breaks.length) {
                              applySecond(null, []);
                              return;
                            }

                            const styleObj = {
                              breaks,
                              colorRamp: metaColors.slice(),
                              opacity: 1,
                              hatched: true,
                            };

                            saveSldAndApply(styleObj, "categorical");
                            return;
                          }

                          // 2) fallback: unique values stats-ból (szöveg/numerikus is), de marad categorical
                          calculateFieldStats(
                            "http://193.225.239.5/geoserver/allum_project/ows",
                            layerId,
                            v
                          )
                            .then((stats) => {
                              const vals = (stats.values || []).filter(
                                (x) => x !== null && x !== undefined && String(x) !== "NaN"
                              );
                              const uniq = [...new Set(vals)];

                              if (!uniq.length) {
                                applySecond(null, []);
                                return;
                              }

                              const ramp =
                                (props.map.colorRamps &&
                                  props.map.colorRamps[props.map.colorRamp] &&
                                  props.map.colorRamps[props.map.colorRamp].colors) ||
                                ["#000000"];

                              const breaks = uniq.map((val, idx) => ({
                                name: String(val),
                                values: [val, val],
                                color: ColorUtils.getColor(idx, uniq.length, ramp),
                                type: "categorical",
                              }));

                              const styleObj = {
                                breaks,
                                colorRamp: ramp.slice ? ramp.slice() : ramp,
                                opacity: 1,
                                hatched: true,
                              };

                              saveSldAndApply(styleObj, "categorical");
                            })
                            .catch((err) => {
                              console.warn("Second categorical fallback stats failed", err);
                              applySecond(null, []);
                            });

                          return;
                        }

                        // --------------------------
                        // ✅ SECOND RANGE: hagyjuk a meglévő (jó) logikát
                        // (meta -> range, különben percentilis)
                        // --------------------------
                        const finishRange = (breaks, colors) => {
                          if (!breaks.length || !colors.length) {
                            applySecond(null, []);
                            return;
                          }
                        
                          const styledBreaks = breaks
                            .map((b, idx) => {
                              const next = idx + 1 < breaks.length ? breaks[idx + 1] : null;
                              if (next == null && idx !== 0) return null;
                        
                              const from = Number(b);
                              const toRaw = next != null ? Number(next) : null;
                              if (!Number.isFinite(from)) return null;
                        
                              const isCountField =
                                typeof v === "string" && (v.endsWith("_count") || v.includes("count"));
                        
                              // ✅ Itt a lényeg: COUNT -> inclusive felső határ (next-1)
                              let rangeFrom = from;
                              let rangeTo = toRaw;
                        
                              if (isCountField) {
                                if (toRaw != null && Number.isFinite(toRaw)) {
                                  rangeTo = toRaw - 1;        // [from, next) -> [from, next-1]
                                } else {
                                  rangeTo = null;             // utolsó bin: ≥ from
                                }
                              }
                        
                              // ---- label ----
                              let label;
                        
                              if (isFolkBaseLayer && isCountField) {
                                // COUNT (folk) – legyen összhangban a rangeTo-val (inclusive)
                                if (idx === 0 && from === 0) {
                                  label = "0";
                                } else if (rangeTo == null) {
                                  label = `≥ ${from}`;
                                } else if (from === rangeTo) {
                                  label = `${from}`;
                                } else {
                                  label = `${from} – ${rangeTo}`;
                                }
                              } else if (isFolkBaseLayer) {
                                // folk, de nem-count mező: maradhat a korábbi (ha itt is elcsúszás van, ugyanígy kezeljük)
                                if (toRaw == null) {
                                  label = `${from}`;
                                } else if (idx === breaks.length - 2) {
                                  label = `${from} – ${toRaw}`; // ne +1
                                } else {
                                  label = from === toRaw ? `${from}` : `${from} – ${toRaw}`;
                                }
                              } else {
                                // minden más rétegnél marad a régi logika
                                const to = toRaw;
                                label =
                                  idx === 0
                                    ? `< ${+to}`
                                    : idx === breaks.length - 2
                                    ? `> ${+from}`
                                    : `${+from} - ${+to}`;
                              }
                        
                              // ✅ SZÍN: maradhat
                              const rampN = Math.max(2, breaks.length);
                              const color = ColorUtils.getColor(
                                idx,
                                rampN,
                                colors.length ? colors : ["#000000"]
                              );
                        
                              return {
                                name: label,
                                values: [rangeFrom, rangeTo], // ✅ COUNT esetén már inclusive
                                color,
                                type: "range",
                              };
                            })
                            .filter(Boolean);
                        
                          if (!styledBreaks.length) {
                            applySecond(null, []);
                            return;
                          }
                        
                          const styleObj = {
                            breaks: styledBreaks,
                            colorRamp: colors.slice(),
                            opacity: 1,
                            hatched: true,
                          };
                        
                          saveSldAndApply(styleObj, "range");
                        };

                        if (metaBreaks.length && metaColors.length) {
                          finishRange(metaBreaks, metaColors);
                          return;
                        }

                        calculateFieldStats(
                          "http://193.225.239.5/geoserver/allum_project/ows",
                          layerId,
                          v
                        )
                          .then((stats) => {
                            const values = (stats.values || [])
                            .filter((v) =>
                              v !== null &&
                              v !== undefined &&
                              String(v).trim() !== "" &&
                              String(v).toLowerCase() !== "nan"
                            )
                            .map((v) => Number(v))
                            .filter((v) => Number.isFinite(v));

                            if (!values.length) {
                              finishRange([], []);
                              return;
                            }

                            const isCountField =
                            typeof v === "string" &&
                            (v.endsWith("_count") || v.includes("count"));
                          
                            const steps =
                              props.map.division === "quantile"
                                ? [0, 1/6, 2/6, 3/6, 4/6, 5/6, 1]
                                : props.map.division === "quintile"
                                ? [0, 0.2, 0.4, 0.6, 0.8, 1]
                                : [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
                            
                            const hasZero = values.some(v => Number(v) === 0);
                            const percentiles = calculatePercentiles(
                              steps,
                              values,
                              isCountField
                                ? { splitZero: hasZero, integerBreaks: true }
                                : { splitZero: false, integerBreaks: false }
                            );
                            
                            if (!percentiles.length) {
                              finishRange([], []);
                              return;
                            }

                            let colors = [];
                            
                            const fieldColorRampMap = {
                              item_count: "colramp05",
                              type_count: "colramp12",
                              style_count: "colramp07",
                              informant_count: "colramp13",
                              collector_count: "colramp09",

                              tancfolyamat_count: "colramp09",
                              kezirat_count: "colramp07",
                              kezirat_page_count: "WhiteBlack",
                              foto_osszes_count: "colramp08",
                              foto_ff_count: "WhiteBlack",
                              foto_szines_count: "colramp13",
                              tanckataszter_count: "colramp07",

                            };
                            
                            const forcedRampName =
                              fieldColorRampMap[v] || props.map.colorRamp;
                            
                            const cr = props.map.colorRamps?.[forcedRampName];

                            if (cr && Array.isArray(cr.colors) && cr.colors.length) {
                              colors = cr.colors.slice();
                            }
                            if (!colors.length) {
                              colors = percentiles.map((_, idx) =>
                                ColorUtils.getColor(idx, percentiles.length, ["#000000"])
                              );
                            }

                            finishRange(percentiles, colors);
                          })
                          .catch((err) => {
                            console.warn("Second range calculateFieldStats failed", err);
                            finishRange([], []);
                          });
                      }}
                    >
                      {itemList.map((item) => (
                        <span key={item.name}>
                          {choloplethFields.map((cf) => {
                            let found = "";
                            if (cf === "val") {
                              const lidShort = (thematicLayerId || "").replace("allum_project", "");
                              found = preloaded.find((obj) => obj.name === lidShort);
                            } else {
                              found = preloaded.find((obj) => obj.name === cf);
                            }

                            if (item.name === cf) {
                              return (
                                <div key={cf}>
                                  <Divider />
                                  <FormControlLabel
                                    value={cf}
                                    style={{ marginLeft: 0, marginTop: 10, marginBottom: 10 }}
                                    control={<Radio size="small" style={{ display: "block" }} />}
                                    label={found?.title || cf}
                                  />
                                </div>
                              );
                            }
                            return null;
                          })}
                        </span>
                      ))}
                    </RadioGroup>
                  </AccordionDetails>
                </Accordion>
              );
            }
          } else {
            // ------------------
            // 2) GRID idősíkok
            // ------------------
            {
              itemList.map((item) =>
                Object.entries(props.map.layers).map((cf) => {
                  if (item.name === cf[1].name) {
                    if (item.category in cat) cat[category].push(item);
                    else cat[category] = [item];
                  }
                })
              );
            }

            if (category !== "undefined" && cat[category]?.length > 0) {
              return (
                <Accordion key={category}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {category}
                  </AccordionSummary>
                  <AccordionDetails>
                    <RadioGroup
                      aria-labelledby="grid-layers"
                      name="grid-layers"
                      value={selectedGrid}
                      onChange={(e) => {
                        const v = e.target.value;

                        if (targetLayer === 1 || !showDual) {
                          // MAIN grid – maga a tematikaváltás
                          dispatch(toggleLayerState(v));
                        } else {
                          // SECOND grid – (NEM nyúlunk hozzá, marad a kiváló logika)
                          if (typeof window === "undefined") return;

                          const layerCfg = props.map.layers?.[v];
                          const url = layerCfg?.url || window.__dualLayer?.layerUrl || null;

                          const applyDual = (maybeSldId, legendItems) => {
                            window.__dualLayer = {
                              ...(window.__dualLayer || {}),
                              enabled: true,
                              layerId: v,
                              layerUrl: url,
                              fieldName: "val",
                              ...(maybeSldId ? { sldId: maybeSldId } : {}),
                              ...(legendItems ? { legend: { items: legendItems } } : {}),
                            };

                            window.dispatchEvent(
                              new CustomEvent("dualLayerChanged", {
                                detail: {
                                  reason: "gridSecondChanged",
                                  layerId: v,
                                  layerUrl: url,
                                  fieldName: "val",
                                  sldId: maybeSldId || null,
                                },
                              })
                            );
                            window.dispatchEvent(
                              new CustomEvent("secondLegendChanged", {
                                detail: { layerId: v, fieldName: "val", sldId: maybeSldId || null },
                              })
                            );
                          };

                          const existingSldId = layerCfg?.sldBody || null;
                          if (existingSldId) {
                            let legendItems = null;
                            if (
                              props.map.activeLayer === v &&
                              props.map.styleData &&
                              Array.isArray(props.map.styleData.breaks)
                            ) {
                              legendItems = props.map.styleData.breaks.map((b) => ({
                                name: b.name,
                                color: b.color,
                              }));
                            }
                            applyDual(existingSldId, legendItems);
                            return;
                          }

                          if (!url) {
                            applyDual(null, null);
                            return;
                          }

                          calculateFieldStats(
                            "http://193.225.239.5/geoserver/allum_project/ows",
                            v,
                            "val"
                          )
                            .then((stats) => {
                              const values = (stats.values || [])
                              .filter((v) =>
                                v !== null &&
                                v !== undefined &&
                                String(v).trim() !== "" &&
                                String(v).toLowerCase() !== "nan"
                              )
                              .map((v) => Number(v))
                              .filter((v) => Number.isFinite(v));

                              const uniq = [...new Set(values)].sort((a, b) => a - b);
                              if (!uniq.length) {
                                applyDual(null, null);
                                return;
                              }

                              const baseRamp =
                                (props.map.colorRamps &&
                                  props.map.colorRamps.colrampgrid &&
                                  props.map.colorRamps.colrampgrid.colors) ||
                                [];

                              const colors = uniq.map((_, idx) =>
                                ColorUtils.getColor(
                                  idx,
                                  uniq.length,
                                  baseRamp.length ? baseRamp : ["#000000"]
                                )
                              );

                              const breaks = uniq.map((value, idx) => ({
                                name: `${value}`,
                                values: [value, value],
                                color: colors[idx],
                                type: "categorical",
                              }));

                              const style = {
                                breaks,
                                colorRamp: colors,
                                opacity: 1,
                                hatched: true,
                              };

                              const sld = generateSld(
                                {
                                  qualifiedName: v,
                                  name: v.split(":")[1],
                                  fieldName: "val",
                                  style,
                                },
                                "categorical"
                              );

                              fetch("http://193.225.239.5/backend2/saveSld", {
                                method: "POST",
                                headers: {
                                  Accept: "application/json",
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({ sldData: sld }),
                              })
                                .then((r) => r.json())
                                .then((r) => {
                                  if (!r.error && r.data?.id) {
                                    const sldId = r.data.id;
                                    const legendItems = breaks.map((b) => ({
                                      name: b.name,
                                      color: b.color,
                                    }));
                                    applyDual(sldId, legendItems);
                                  } else {
                                    console.warn("Second grid SLD save error (stats-based)", r);
                                    applyDual(null, null);
                                  }
                                })
                                .catch((err) => {
                                  console.warn("Second grid SLD save failed (stats-based)", err);
                                  applyDual(null, null);
                                });
                            })
                            .catch((err) => {
                              console.warn("Second grid calculateFieldStats failed", err);
                              applyDual(null, null);
                            });
                        }
                      }}
                    >
                      {itemList.map((item) => (
                        <span key={item.name}>
                          {Object.entries(props.map.layers).map((entry2) => {
                            if (entry2[1].id.includes("kulonb") || entry2[1].id.includes("grid")) {
                              const found = preloaded.find(
                                (obj) => obj.name === entry2[1].id.replace("allum_project:", "")
                              );

                              if (item.name === entry2[1].name) {
                                return (
                                  <div key={entry2[1].id}>
                                    <Divider />
                                    <FormControlLabel
                                      value={entry2[1].id}
                                      style={{ marginLeft: 0, marginTop: 10, marginBottom: 10 }}
                                      control={<Radio size="small" style={{ display: "block" }} />}
                                      label={found?.title || entry2[1].name}
                                    />
                                  </div>
                                );
                              }
                            }
                            return null;
                          })}
                        </span>
                      ))}
                    </RadioGroup>
                  </AccordionDetails>
                </Accordion>
              );
            }
          }

          return null;
        })}
      </div>
    )}
    </div>
  );
}

export default connect(mapStateToProps)(LayerSelection);
